import React, { useMemo, useState } from 'react';

import type { GroupRecord } from '@vlabs/api/artemy/model';
import {
  ChevronDownIcon, ChevronUpIcon,
} from '@vlabs/icons';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';

import {
  DEFAULT_STREAMS_PAGE_SIZE,
  SourceFiltersMenu,
  SourceFiltersProvider,
  SourceFilterMenuButton,
  SourceGroupSearchInput,
  SourceGroupSortDropdown,
  SourceFastFilters,
  useGroupStreamsCounts,
} from '@vlabs/monokit/entities/sources';
import { CreateStreamButton } from '@vlabs/monokit/features/create-stream';
import { GoToSourceGroupButton } from '@vlabs/monokit/features/go-to-source-group';
import { StartStopGroupStreamsButtons } from '@vlabs/monokit/features/start-stop-group-streams';

import { useSourceGroupListViewerTranslation } from '../i18n';
import st from './SourceGroupCard.module.sass';
import { PermissionTag } from '../../../entities/permission-tags';
import { buildStreamsColumns } from '../stream-list-table/columns';
import { GenericStreamListTable } from '../stream-list-table/GenericStreamListTable';

interface SourceGroupCardProps {
  group: GroupRecord;
  isOpen: boolean;
  onToggle: VoidFunction;
}

// Счётчики «Источников: N (В работе: M)» нужны и для свёрнутых групп
function SourceGroupStats({ group }: { group: GroupRecord }): React.JSX.Element {
  const { t } = useSourceGroupListViewerTranslation();
  const { total, inProgress } = useGroupStreamsCounts(group.group_id);

  return (
    <span className={st.GroupStats}>
      {t('stream_count', { count: total })}
      <span className={st.GroupStats_positive}>{t('in_work_count', { count: inProgress })}</span>
    </span>
  );
}

function SourceGroupCard({
  group,
  isOpen,
  onToggle,
}: SourceGroupCardProps): React.JSX.Element {
  const groupId = group.group_id;
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { t } = useSourceGroupListViewerTranslation();

  const columns = useMemo(
    () => buildStreamsColumns({ t, group }),
    [group, t],
  );

  return (
    <SourceFiltersProvider
      initialData={{
        group_id: groupId,
        page: 1,
        page_size: DEFAULT_STREAMS_PAGE_SIZE,
        order: 'desc',
      }}
    >
      <div className={st.SourceGroupCard}>
        <div className={st.Heading}>
          <div className={st.HeadingLeft}>
            <h6 className={st.GroupName}>
              {group.group_name}
              <IconButton
                data-testid={`toggle-group-${groupId}`}
                icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                kind="secondary"
                onClick={onToggle}
                shape="square"
                size="small"
                variant="flat"
              />
            </h6>
            <SourceGroupStats group={group} />
            {!!group.permission_tag && <PermissionTag permissionTag={group.permission_tag} />}
            <SourceGroupSearchInput />
          </div>
          <div className={st.HeadingActions}>
            {!!isOpen && <SourceGroupSortDropdown />}
            <StartStopGroupStreamsButtons groupId={groupId} />
            <GoToSourceGroupButton groupId={groupId} />
            <CreateStreamButton groupId={groupId} />
            {!!isOpen && (
              <SourceFilterMenuButton
                data-testid={`source-filter-${groupId}`}
                isOpen={isFiltersOpen}
                onToggle={() => setIsFiltersOpen((prev) => !prev)}
              />
            )}
          </div>

          {!!isOpen && !!isFiltersOpen && (
            <div className={st.FilterMenuWrap}>
              <SourceFiltersMenu onClose={() => setIsFiltersOpen(false)} />
            </div>
          )}
        </div>

        {!!isOpen && (
          <div className={st.Content}>
            <SourceFastFilters />
            <GenericStreamListTable
              columns={columns}
              groupId={groupId}
            />
          </div>
        )}
      </div>
    </SourceFiltersProvider>
  );
}

export { SourceGroupCard };
